import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_nearby_walkers,
  fetch_support_counts,
  fetch_support_request_detail,
  fetch_support_request_list,
  propose_slot,
  reassign_walk,
  refund_session,
} from './api';
import type { SupportRequestListFilters } from './types';
import type { SortState } from '@/hooks/use-url-state';

const KEYS = {
  list: (filters: SupportRequestListFilters) => ['admin-support-list', filters] as const,
  detail: (id: string) => ['admin-support-detail', id] as const,
  nearby: (id: string) => ['admin-support-nearby', id] as const,
};

export function use_support_request_list(filters: SupportRequestListFilters, sort?: SortState) {
  return useQuery({
    queryKey: [...KEYS.list(filters), sort],
    queryFn: () => fetch_support_request_list(filters, sort),
    placeholderData: (prev) => prev,
  });
}

export function use_support_request_detail(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['admin-support-detail', 'noop'],
    queryFn: () => fetch_support_request_detail(id as string),
    enabled: typeof id === 'string' && id.length > 0,
  });
}

export function use_nearby_walkers(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: id ? KEYS.nearby(id) : ['admin-support-nearby', 'noop'],
    queryFn: () => fetch_nearby_walkers(id as string),
    enabled: enabled && typeof id === 'string' && id.length > 0,
  });
}

export function use_support_actions(id: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    qc.invalidateQueries({ queryKey: ['admin-support-list'] });
  };
  const reassign = useMutation({
    mutationFn: (input: { vendor_profile_id: string; payout_amount_inr: number }) =>
      reassign_walk(id, input),
    onSuccess: invalidate,
  });
  const propose = useMutation({
    mutationFn: (slot_start: string) => propose_slot(id, slot_start),
    onSuccess: invalidate,
  });
  const refund = useMutation({
    mutationFn: (amount_inr?: number) => refund_session(id, amount_inr),
    onSuccess: invalidate,
  });
  return { reassign, propose, refund };
}

/** Counts for the queue's tiles, including how long the oldest has waited. */
export function use_support_counts() {
  return useQuery({ queryKey: ['admin-support-counts'], queryFn: fetch_support_counts });
}
